/**
 * The NCR page's workflow tab row: 8D Report, then each process that applies
 * to this NCR with a badge for where it has got to. E24 (QUALITY-NCR-REVIEW E2E
 * run): a process that doesn't apply gets no tab, as Replacement already did.
 * Extracted from NCREdit.tsx, which was over the 500-line limit.
 */
import type { NonConformanceReport } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { TabsList, TabsTrigger } from '../../components/ui/tabs';
import { FileWarning, Package, CreditCard, PackageCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { plateBadgeFor } from './tabBadges';
import type { PlateKey } from '../../components/quality/ncrPlate';
import { visibleWorkflowTabs, type WorkflowTab } from './workflowTabs';

interface WorkflowTabsListProps {
  ncr: NonConformanceReport;
}

// T9: every tab's badge is the grid's label for that process (plateBadgeFor).
const TAB_META: Record<WorkflowTab, { label: string; icon: LucideIcon; plate: PlateKey }> = {
  '8d_report': { label: '8D Report', icon: FileWarning, plate: 'eightd' },
  collection: { label: 'Collection', icon: Package, plate: 'collection' },
  credit: { label: 'Credit', icon: CreditCard, plate: 'credit' },
  replacement: { label: 'Replacement', icon: PackageCheck, plate: 'replacement' },
};

// Full class names so Tailwind keeps them.
const GRID_COLS: Record<number, string> = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' };

/**
 * The tab triggers for the NCR's workflow tabs.
 *
 * @param props.ncr - the NCR the page shows
 * @returns the tab row
 */
export function WorkflowTabsList({ ncr }: WorkflowTabsListProps) {
  const tabs = visibleWorkflowTabs(ncr);
  return (
    <TabsList className={`grid w-full ${GRID_COLS[tabs.length]}`}>
      {tabs.map((tab) => {
        const { label, icon: Icon, plate } = TAB_META[tab];
        const b = plateBadgeFor(ncr, plate);
        return (
          <TabsTrigger key={tab} value={tab} className="flex items-center gap-2">
            <Icon className="h-4 w-4" />
            {label}
            {b && (
              <Badge variant={b.variant} className={`ml-1 text-[10px] px-1.5 py-0 ${b.tone ?? ''}`}>
                {b.label}
              </Badge>
            )}
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
}
